import {
  DatePicker,
  Button,
  message,
  Select,
  Input,
  Card,
  Row,
  Col,
  Statistic,
  Skeleton,
} from "antd";
import axios from "axios";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import {
  FaFilter,
  FaBox,
  FaCheckCircle,
  FaExclamationCircle,
  FaPrint,
  FaEdit,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import LoadingBox from "../../Components/LoadingBox";
import { API, Headers } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
import DataTable from "./DataTable";
import UpdateStockModal from "./UpdateStockModal";
import "./style.css";

const normalizeServiceTypeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value || value === 0) {
    return [value];
  }
  return [];
};

function Stock() {
  const { status } = useParams();
  const [loading, setLoading] = useState(true);
  const [indicator, setIndicator] = useState(true);
  const [serviceTypeLoading, setServiceTypeLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [filter, setFilter] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [serviceListLoading, setServiceListLoading] = useState(true);
  const [fullData, setFullData] = useState([]);
  const [filters, setFilters] = useState({
    searchQuery: "",
    serviceType: [], // Stores applied service type filters
    selectedUser: null,
    selectedCountry: null,
    selectedBranch: null,
    assignmentFilter: status === "assigned" ? "assigned" : "unassigned",
    dateRange: [null, null],
  });
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(
    normalizeServiceTypeArray([])
  );
  const [users, setUsers] = useState([]);
  const [stockCount, setStockCount] = useState(0);
  const [country, setCountry] = useState([]);
  const [branches, setBranches] = useState([]);
  const [counts, setCounts] = useState({
    totalStocks: 0,
    assignedStocks: 0,
    unassignedStocks: 0,
  });
  const [updateStockModalVisible, setUpdateStockModalVisible] = useState(false);

  const Auth = useSelector((state) => state.Auth.user);
  const isAdmin = Auth?.id === "1";
  const searchTimeoutRef = useRef(null);

  // Function to calculate service type summaries from full data
  const calculateServiceTypeSummaries = useCallback((fullData) => {
    if (!fullData || !Array.isArray(fullData) || fullData.length === 0) {
      return [];
    }

    const summaries = {};

    fullData.forEach((item) => {
      if (!item || typeof item !== "object") return;

      const serviceName = item.service?.name || "Unknown";
      if (!summaries[serviceName]) {
        summaries[serviceName] = {
          name: serviceName,
          count: 0,
          totalWeight: 0,
          totalCharge: 0,
        };
      }
      summaries[serviceName].count += 1;
      summaries[serviceName].totalWeight +=
        parseFloat(item.chargable_weight) || 0;
      summaries[serviceName].totalCharge += parseFloat(item.total_charge) || 0;
    });

    return Object.values(summaries).sort((a, b) => b.count - a.count);
  }, []);

  // Memoized service type summaries to prevent unnecessary recalculations
  const memoizedServiceTypeSummaries = useMemo(() => {
    return calculateServiceTypeSummaries(fullData);
  }, [fullData, calculateServiceTypeSummaries]);

  const appliedServiceTypes = useMemo(() => {
    return normalizeServiceTypeArray(filters.serviceType);
  }, [filters.serviceType]);

  const hasPendingServiceTypeChanges = useMemo(() => {
    if (selectedServiceTypes.length !== appliedServiceTypes.length) {
      return true;
    }
    const appliedSet = new Set(appliedServiceTypes);
    return selectedServiceTypes.some((id) => !appliedSet.has(id));
  }, [selectedServiceTypes, appliedServiceTypes]);

  // Load service type summaries
  const loadServiceTypeSummaries = useCallback(
    async (filterParams) => {
      setServiceTypeLoading(true);
      const {
        searchQuery,
        serviceType,
        selectedUser,
        selectedCountry,
        selectedBranch,
        assignmentFilter,
        dateRange,
      } = filterParams;

      // Format service_type as comma-separated string if array has values
      const serviceTypeParam =
        Array.isArray(serviceType) && serviceType.length > 0
          ? serviceType.join(",")
          : undefined;

      const reqObj = {
        box_number: searchQuery || "",
        service_type: serviceTypeParam,
        country: selectedCountry || undefined,
        auth_id: Auth?.id || undefined,
        assigned: assignmentFilter || undefined,
      };

      // Updated branch and user filtering logic
      if (isAdmin) {
        // For admin users
        if (selectedBranch) {
          reqObj.branch_id = selectedBranch;
        }
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      } else {
        // For non-admin users
        reqObj.branch_id = Auth?.branch_id;
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      }

      if (dateRange[0] && dateRange[1]) {
        reqObj.start_date = dateRange[0].toISOString().split("T")[0];
        reqObj.end_date = dateRange[1].toISOString().split("T")[0];
      }

      // Pass branch_id as query param for backend route matching
      // Pass branch_id as query param for backend route matching
      const queryParams = new URLSearchParams({ order: "DESC" });

      // For admin users, if branch is selected, pass it as query param too
      if (isAdmin && selectedBranch) {
        queryParams.append("branch_id", selectedBranch);
      } else if (!isAdmin && Auth?.branch_id) {
        queryParams.append("branch_id", Auth.branch_id);
      }

      // For admin users, if branch is selected, pass it as query param too
      if (isAdmin && selectedBranch) {
        queryParams.append("branch_id", selectedBranch);
      } else if (!isAdmin && Auth?.branch_id) {
        queryParams.append("branch_id", Auth.branch_id);
      }

      try {
        const response = await axios.post(
          `${API.BASR_URL}${
            API.BOXES_VIA_BRANCH_ID_PGNTN_ALL
          }?${queryParams.toString()}`,
          reqObj,
          { headers: Headers() }
        );

        if (response.status === 201) {
          const fullData = response.data.data || [];
          setFullData(fullData);
        } else {
          setFullData([]);
        }
      } catch (error) {
        console.error("Error loading service type summaries:", error);
        setFullData([]);
      } finally {
        setServiceTypeLoading(false);
      }
    },
    [Auth, isAdmin, calculateServiceTypeSummaries]
  );

  // Load data function
  const loadData = useCallback(
    (page, take, filterParams) => {
      setIndicator(true);
      const {
        searchQuery,
        serviceType,
        selectedUser,
        selectedCountry,
        selectedBranch,
        assignmentFilter,
        dateRange,
      } = filterParams;

      // Format service_type as comma-separated string if array has values
      const serviceTypeParam =
        Array.isArray(serviceType) && serviceType.length > 0
          ? serviceType.join(",")
          : undefined;

      const reqObj = {
        box_number: searchQuery || "",
        service_type: serviceTypeParam,
        country: selectedCountry || undefined,
        auth_id: Auth?.id || undefined,
        assigned: assignmentFilter || undefined,
      };

      // Updated branch and user filtering logic
      if (isAdmin) {
        // For admin users
        if (selectedBranch) {
          reqObj.branch_id = selectedBranch;
        }
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      } else {
        // For non-admin users
        reqObj.branch_id = Auth?.branch_id;
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      }

      if (dateRange[0] && dateRange[1]) {
        reqObj.start_date = dateRange[0].toISOString().split("T")[0];
        reqObj.end_date = dateRange[1].toISOString().split("T")[0];
      }

      // Pass branch_id as query param for backend route matching
      const queryParams = new URLSearchParams({
        order: "DESC",
        page: String(page),
        take: String(take),
      });

      // For admin users, if branch is selected, pass it as query param too
      if (isAdmin && selectedBranch) {
        queryParams.append("branch_id", selectedBranch);
      } else if (!isAdmin && Auth?.branch_id) {
        queryParams.append("branch_id", Auth.branch_id);
      }

      axios
        .post(
          `${API.BASR_URL}${API.BOXES_VIA_BRANCH_ID}?${queryParams.toString()}`,
          reqObj,
          { headers: Headers() }
        )
        .then((response) => {
          if (response.status === 201) {
            setData(response.data.data || []);
            setMeta(response.data.meta || {});

            if (assignmentFilter === "assigned") {
              setStockCount(response.data.counts?.assignedStocks || 0);
            } else {
              setStockCount(response.data.counts?.unassignedStocks || 0);
            }
            setCounts(
              response.data.counts || {
                totalStocks: 0,
                assignedStocks: 0,
                unassignedStocks: 0,
              }
            );
            message.success("Data loaded successfully");
          } else {
            message.error("Failed to load data");
            setData([]);
            setMeta({});
            setStockCount(0);
            setCounts({
              totalStocks: 0,
              assignedStocks: 0,
              unassignedStocks: 0,
            });
          }
          setLoading(false);
          setIndicator(false);
        })
        .catch((error) => {
          message.error("Error loading data");
          console.error(error);
          setData([]);
          setMeta({});
          setStockCount(0);
          setCounts({
            totalStocks: 0,
            assignedStocks: 0,
            unassignedStocks: 0,
          });
          setLoading(false);
          setIndicator(false);
        });
    },
    [Auth, isAdmin]
  );

  // Debounced search handler
  const handleSearchChange = useCallback(
    (value) => {
      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        const newFilters = { ...filters, searchQuery: value };
        setFilters(newFilters);
        setPage(1);
        loadData(1, take, newFilters);
        loadServiceTypeSummaries(newFilters);
      }, 300);
    },
    [filters, take, loadData, loadServiceTypeSummaries]
  );

  // Consolidated filter handler for non-search filters
  const handleFilterChange = useCallback(
    (key, value) => {
      const normalizedValue =
        key === "serviceType" ? normalizeServiceTypeArray(value) : value;

      if (key === "serviceType") {
        setSelectedServiceTypes(normalizedValue);
      }

      const newFilters = {
        ...filters,
        [key]: normalizedValue,
      };
      setFilters(newFilters);
      setPage(1);
      loadData(1, take, newFilters);
      loadServiceTypeSummaries(newFilters);
    },
    [filters, take, loadData, loadServiceTypeSummaries]
  );

  // Handle service type card click
  const handleServiceTypeClick = useCallback(
    (serviceName) => {
      const serviceType = serviceList.find(
        (service) => service.name === serviceName
      );

      if (serviceType) {
        setSelectedServiceTypes((prev) => {
          const isAlreadySelected = prev.includes(serviceType.id);
          if (isAlreadySelected) {
            return prev.filter((id) => id !== serviceType.id);
          }
          return [...prev, serviceType.id];
        });
      } else {
        message.error(`Service type "${serviceName}" not found`);
      }
    },
    [serviceList]
  );

  const applyServiceTypeFilters = useCallback(() => {
    if (!hasPendingServiceTypeChanges) {
      message.info("No new service type selection to apply");
      return;
    }

    const newFilters = {
      ...filters,
      serviceType: selectedServiceTypes,
    };

    setFilters(newFilters);
    setPage(1);
    loadData(1, take, newFilters);
    loadServiceTypeSummaries(newFilters);
    message.success("Service type filters applied");
  }, [
    filters,
    selectedServiceTypes,
    hasPendingServiceTypeChanges,
    take,
    loadData,
    loadServiceTypeSummaries,
  ]);

  const clearServiceTypeFilters = useCallback(() => {
    const hasAppliedFilters = appliedServiceTypes.length > 0;
    const hasSelectedFilters = selectedServiceTypes.length > 0;

    if (!hasAppliedFilters && !hasSelectedFilters) {
      message.info("No service type filters to clear");
      return;
    }

    setSelectedServiceTypes([]);

    if (hasAppliedFilters) {
      const newFilters = {
        ...filters,
        serviceType: [],
      };
      setFilters(newFilters);
      setPage(1);
      loadData(1, take, newFilters);
      loadServiceTypeSummaries(newFilters);
      message.success("Service type filters cleared");
    } else {
      message.success("Service type selection cleared");
    }
  }, [
    appliedServiceTypes,
    selectedServiceTypes,
    filters,
    take,
    loadData,
    loadServiceTypeSummaries,
  ]);

  // Check if a service type is currently selected
  const isServiceTypeSelected = useCallback(
    (serviceName) => {
      const serviceType = serviceList.find(
        (service) => service.name === serviceName
      );
      if (!serviceType) return false;

      return selectedServiceTypes.includes(serviceType.id);
    },
    [selectedServiceTypes, serviceList]
  );

  // Load full data for printing
  const loadFullDataForPrint = useCallback(
    async (filterParams) => {
      setIndicator(true);
      const {
        searchQuery,
        serviceType,
        selectedUser,
        selectedCountry,
        selectedBranch,
        assignmentFilter,
        dateRange,
      } = filterParams;

      // Format service_type as comma-separated string if array has values
      const serviceTypeParam =
        Array.isArray(serviceType) && serviceType.length > 0
          ? serviceType.join(",")
          : undefined;

      const reqObj = {
        box_number: searchQuery || "",
        service_type: serviceTypeParam,
        country: selectedCountry || undefined,
        auth_id: Auth?.id || undefined,
        assigned: assignmentFilter || undefined,
      };

      // Updated branch and user filtering logic
      if (isAdmin) {
        if (selectedBranch) {
          reqObj.branch_id = selectedBranch;
        }
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      } else {
        reqObj.branch_id = Auth?.branch_id;
        if (selectedUser) {
          reqObj.userid = selectedUser;
        }
      }

      if (dateRange[0] && dateRange[1]) {
        reqObj.start_date = dateRange[0].toISOString().split("T")[0];
        reqObj.end_date = dateRange[1].toISOString().split("T")[0];
      }

      const queryParams = new URLSearchParams({ order: "DESC" });

      try {
        const response = await axios.post(
          `${API.BASR_URL}${
            API.BOXES_VIA_BRANCH_ID_PGNTN_ALL
          }?${queryParams.toString()}`,
          reqObj,
          { headers: Headers() }
        );
        if (response.status === 201) {
          return response.data.data || [];
        } else {
          message.error("Failed to load full data for print");
          return [];
        }
      } catch (error) {
        message.error("Error loading full data for print");
        console.error(error);
        return [];
      } finally {
        setIndicator(false);
      }
    },
    [Auth, isAdmin]
  );

  // Load initial data
  useEffect(() => {
    loadServiceType();
    if (isAdmin) {
      loadBranches();
    }
    loadUsers();
    loadCountry();
    loadData(page, take, filters);
    loadServiceTypeSummaries(filters);
  }, [loadData, loadServiceTypeSummaries, isAdmin, page, take]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const loadServiceType = () => {
    setServiceListLoading(true);
    axios
      .get(`${API.BASR_URL}${API.SERVICE_TYPE_LIST}/list`)
      .then((response) => {
        setServiceList(response?.data || []);
      })
      .catch((error) => {
        message.error("Error loading service types");
        console.error(error);
      })
      .finally(() => {
        setServiceListLoading(false);
      });
  };

  const loadUsers = () => {
    axios
      .get(`${API.BASR_URL}${API.USERS_LIST}/list`, { headers: Headers() })
      .then((response) => {
        if (response.status === 200) {
          setUsers(response.data || []);
        } else {
          message.error("Failed to load users");
        }
      })
      .catch((error) => {
        message.error("Error loading users");
        console.error(error);
      });
  };

  const loadBranches = () => {
    axios
      .get(`${API.BASR_URL}${API.BRANCHES_ACTION}list`, { headers: Headers() })
      .then((response) => {
        if (response.status === 200) {
          setBranches(response.data || []);
        } else {
          message.error("Failed to load branches");
        }
      })
      .catch((error) => {
        message.error("Error loading branches");
        console.error(error);
      });
  };

  const loadCountry = () => {
    axios
      .get(`${API.BASR_URL}${API.COUNTRIES_ACTIONS}list`, {
        headers: Headers(),
      })
      .then((response) => {
        if (response.status === 200) {
          setCountry(response.data || []);
        } else {
          message.error("Failed to load countries");
        }
        setLoading(false);
      })
      .catch((error) => {
        message.error("Error loading countries");
        console.error(error);
        setLoading(false);
      });
  };

  const getLatestBoxStatus = (record) => {
    if (record?.boxStatus && Array?.isArray(record?.boxStatus)) {
      // Sort by created_at descending and get the latest
      const sortedStatus = record?.boxStatus?.sort(
        (a, b) =>
          moment(b?.created_at).valueOf() - moment(a?.created_at).valueOf()
      );
      return sortedStatus[0];
    }
    return null;
  };

  const handlePrint = async () => {
    try {
      const fullData = await loadFullDataForPrint(filters);
      if (!fullData || !Array.isArray(fullData)) {
        message.error("No data available for printing");
        return;
      }
      console.log("fullData --- ", fullData);
      const totalWeight = fullData.reduce(
        (sum, item) => sum + (parseFloat(item?.chargable_weight) || 0),
        0
      );

      const serviceTypeSummaries = calculateServiceTypeSummaries(fullData);

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(`
           <html>
             <head>
               <title>Stock Bill</title>
               <style>
                 body { font-family: Arial, sans-serif; margin: 0; padding: 10px; background: #fff; }
                 .bill-container { max-width: 1000px; margin: 0 auto; border: 1px solid grey; padding: 15px; }
                 .bill-header { text-align: center; border-bottom: 1px solid grey; padding-bottom: 8px; margin-bottom: 15px; }
                 .bill-header h1 { margin: 0; font-size: 24px; font-weight: bold; }
                 .bill-header p { margin: 3px 0; color: #333; font-size: 12px; }
                 .company-details { text-align: left; margin-bottom: 15px; }
                 .company-details p { margin: 1px 0; font-size: 11px; }
                 .summary-table, .stock-table, .service-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                 .summary-table th, .summary-table td, .stock-table th, .stock-table td, .service-table th, .service-table td { 
                   border: 1px solid #000; padding: 4px 6px; text-align: left; font-size: 11px; 
                 }
                 .summary-table th, .stock-table th, .service-table th { background-color: #f5f5f5; font-weight: bold; }
                 .totals { text-align: right; margin-top: 15px; font-weight: bold; font-size: 12px; }
                 .footer { text-align: center; margin-top: 15px; border-top: 1px solid grey; padding-top: 8px; color: #555; font-size: 10px; }
                 h2 { font-size: 16px; margin: 10px 0 5px 0; }
                 @media print { body { margin: 0; padding: 0; } }
               </style>
             </head>
             <body>
               <div class="bill-container">
                 <div class="bill-header">
                   <h1>Stock Bill</h1>
                   <p>Generated by: ${Auth?.name || "Unknown User"}</p>
                   <p>Date: ${new Date().toLocaleString("en-IN", {
                     timeZone: "Asia/Kolkata",
                   })}</p>
                   <p><strong>${
                     filters.assignmentFilter === "assigned"
                       ? "Assigned Boxes"
                       : "Unassigned Boxes"
                   }</strong></p>
                 </div>
                 <div class="company-details">
                   <p><strong>Company Name:</strong> Sahari Cargo</p>
                   <p><strong>Address:</strong> SHARAFIYA-JEDDAH</p>
                   <p><strong>Contact:</strong> 050 521 6943</p>
                 </div>
               
                 <h2>Stock Items</h2>
                 <table class="stock-table">
                   <thead>
                     <tr>
                       <th>SL. NO</th>
                       <th>BOOKING ID</th>
                       <th>BOX NUMBER</th>
                       <th>BOX SIZE</th>
                       <th>BOX WEIGHT</th>
                       <th>SERVICE</th>
                       <th>BOOKING DATE</th>
                       <th>RECEIVE DATE</th>
                       <th>USER</th>
                     </tr>
                   </thead>

                  

                  <tbody>
                    ${fullData
                      ?.map((item, index) => {
                        const latestBoxStatus = getLatestBoxStatus(item);
                        return `<tr>
                        <td>${index + 1}</td>
                         <td>${item?.collection_id || "-"}</td>
                         <td>${item?.box_number || "-"}</td>
                         <td>${item?.boxType?.name || "-"}</td>
                         <td>${item?.chargable_weight || 0}</td>
                         <td>${item?.service?.name || "-"}</td>
                        <td>
                          ${
                            item?.createdAt
                              ? moment(item.createdAt).format("DD-MM-YYYY")
                              : "-"
                          }
                        </td>
                        <td>
                          ${
                            latestBoxStatus && latestBoxStatus?.created_at
                              ? moment(latestBoxStatus?.created_at).format(
                                  "DD-MM-YYYY"
                                )
                              : "-"
                          }
                        <td>${
                          item?.created_user?.name?.toUpperCase() || "-"
                        }</td>
                        </td>
                      </tr>`;
                      })
                      .join("")}
                  </tbody>
                 </table>
                 <div class="totals">
                   <p>Total Weight: ${totalWeight.toFixed(2)} kg</p>
                   <p>Total Pcs: ${fullData.length} pieces</p>
                 </div>
                 <div class="footer">
                   <p>Thank you for your business!</p>
                   <p>Generated by Sahari Cargo</p>
                 </div>
               </div>
               <script>
                 window.onload = () => { window.print(); setTimeout(() => window.close(), 1000); };
               </script>
             </body>
           </html>
         `);
        printWindow.document.close();
      } else {
        message.error("Failed to open print window. Please allow pop-ups.");
      }
    } catch (error) {
      console.error("Error in print function:", error);
      message.error("Error generating print report");
    }
  };

  // Handle update stock modal
  const handleUpdateStockClick = () => {
    setUpdateStockModalVisible(true);
  };

  const handleUpdateStockSuccess = () => {
    // Reload data after successful update
    loadData(page, take, filters);
    loadServiceTypeSummaries(filters);
  };

  const stockData = [
    {
      title: "Total Stocks",
      value: counts.totalStocks.toString(),
      icon: FaBox,
      type: "total",
    },
    {
      title: "Stocks Assigned",
      value: counts.assignedStocks.toString(),
      icon: FaCheckCircle,
      type: "assigned",
    },
    {
      title: "Stocks Not Assigned",
      value: counts.unassignedStocks.toString(),
      icon: FaExclamationCircle,
      type: "unassigned",
    },
  ];

  return (
    <div>
      <PageHeader
        PageName={"STOCK"}
        breadScrum={"Admin / Stock"}
        loader={indicator}
      />
      <div className="stock-cards-grid">
        {stockData.map((item, index) => {
          const IconComponent = item.icon;
          return (
            <div key={index} className={`stock-card ${item.type}`}>
              <div className="stock-card-header">
                <div className="stock-icon-container">
                  <IconComponent
                    size={20}
                    className={`stock-icon ${item.type}`}
                  />
                </div>
                <div className="stock-status-badge">Active</div>
              </div>
              <div className="stock-card-content">
                <h3 className={`stock-title ${item.type}`}>{item.title}</h3>
                <div className="stock-value-row">
                  <span className={`stock-value ${item.type}`}>
                    {item.value}
                  </span>
                </div>
              </div>
              <div className="stock-card-background">
                <IconComponent
                  size={100}
                  className={`stock-icon ${item.type}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Service Type Summary Section */}
      <div className="service-summary-container">
        <div
          style={{
            marginBottom: 12,
            fontSize: "13px",
            fontWeight: "bold",
            color: "#374151",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <span>Service Type Summary</span>
            {appliedServiceTypes.length > 0 && (
              <span
                style={{
                  fontSize: "11px",
                  color: "#1890ff",
                  fontWeight: "normal",
                }}
              >
                Applied filter:{" "}
                {appliedServiceTypes
                  .map(
                    (id) =>
                      serviceList.find((s) => s.id === id)?.name || "Unknown"
                  )
                  .filter(Boolean)
                  .join(", ")}{" "}
                ({appliedServiceTypes.length} applied)
              </span>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              alignItems: "flex-end",
            }}
          >
            {selectedServiceTypes.length > 0 && (
              <span
                style={{
                  fontSize: "11px",
                  color: hasPendingServiceTypeChanges ? "#fa8c16" : "#52c41a",
                  fontWeight: "normal",
                  textAlign: "right",
                }}
              >
                Selected:{" "}
                {selectedServiceTypes
                  .map(
                    (id) =>
                      serviceList.find((s) => s.id === id)?.name || "Unknown"
                  )
                  .filter(Boolean)
                  .join(", ")}{" "}
                ({hasPendingServiceTypeChanges ? "pending apply" : "applied"})
              </span>
            )}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {selectedServiceTypes.length > 0 && (
                <Button
                  type="default"
                  size="small"
                  onClick={clearServiceTypeFilters}
                >
                  Clear
                </Button>
              )}
              <Button
                type="primary"
                size="small"
                disabled={!hasPendingServiceTypeChanges}
                onClick={applyServiceTypeFilters}
              >
                Apply Service Filter
              </Button>
            </div>
          </div>
        </div>
        {serviceTypeLoading ? (
          <div style={{ padding: "20px 0" }}>
            <Row gutter={[8, 8]}>
              {[1, 2, 3, 4, 5, 6].map((index) => (
                <Col xs={12} sm={8} md={6} lg={4} xl={3} key={index}>
                  <Card size="small" className="service-summary-card">
                    <Skeleton active paragraph={{ rows: 1 }} />
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        ) : memoizedServiceTypeSummaries?.length > 0 ? (
          <Row gutter={[8, 8]}>
            {memoizedServiceTypeSummaries.map((summary, index) => (
              <Col xs={12} sm={8} md={6} lg={4} xl={3} key={index}>
                <Card
                  size="small"
                  className="service-summary-card"
                  onClick={() => handleServiceTypeClick(summary.name)}
                  style={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: isServiceTypeSelected(summary.name)
                      ? "2px solid #1890ff"
                      : "1px solid #e8e8e8",
                    backgroundColor: isServiceTypeSelected(summary.name)
                      ? "#f0f8ff"
                      : "#fff",
                    transform: isServiceTypeSelected(summary.name)
                      ? "scale(1.02)"
                      : "scale(1)",
                    boxShadow: isServiceTypeSelected(summary.name)
                      ? "0 4px 12px rgba(24, 144, 255, 0.2)"
                      : "0 2px 8px rgba(0,0,0,0.09)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isServiceTypeSelected(summary.name)) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 12px rgba(0,0,0,0.15)";
                      e.currentTarget.style.borderColor = "#1890ff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isServiceTypeSelected(summary.name)) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(0,0,0,0.09)";
                      e.currentTarget.style.borderColor = "#e8e8e8";
                    } else {
                      // Keep selected state on hover
                      e.currentTarget.style.borderColor = "#1890ff";
                    }
                  }}
                >
                  <div className="service-summary-statistic">
                    <Statistic
                      title={summary.name}
                      value={summary.count}
                      suffix="items"
                      valueStyle={{
                        color: "#374151",
                        fontSize: "16px",
                        fontWeight: "600",
                      }}
                    />
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        color: "#666",
                        fontWeight: "500",
                      }}
                    >
                      <b>{summary.totalWeight?.toFixed(2) || "0.00"} kg</b>
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              background: "#fafafa",
              borderRadius: "8px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            No service type data available
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>Total {stockCount || 0} Stocks</div>
        <div style={{ display: "flex", gap: "12px" }}>
          {isAdmin && (
            <Button
              type="default"
              icon={<FaEdit />}
              onClick={handleUpdateStockClick}
              style={{ marginBottom: 16 }}
            >
              &nbsp; Update Stock
            </Button>
          )}
          <Button
            type="primary"
            icon={<FaPrint />}
            onClick={handlePrint}
            style={{ marginBottom: 16 }}
          >
            &nbsp; Print Report
          </Button>
        </div>
      </div>
      <div className="stock-Searchmain">
        {filter && (
          <>
            <Select
              bordered
              showSearch
              placeholder="Select Assignment"
              style={{ width: 200, borderRadius: 6, marginRight: 10 }}
              onChange={(value) =>
                handleFilterChange("assignmentFilter", value)
              }
              value={filters.assignmentFilter}
              allowClear
            >
              <Select.Option value="unassigned">Unassigned</Select.Option>
              <Select.Option value="assigned">Assigned</Select.Option>
            </Select>
            <Select
              bordered
              showSearch
              placeholder="Select User"
              style={{ width: 200, borderRadius: 6, marginRight: 10 }}
              filterOption={(input, option) =>
                (option.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => handleFilterChange("selectedUser", value)}
              value={filters.selectedUser}
              allowClear
            >
              {users.map((option) => (
                <Select.Option key={option.id} value={option.id}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
            {isAdmin && (
              <Select
                bordered
                showSearch
                placeholder="Select Branch"
                style={{ width: 200, borderRadius: 6, marginRight: 10 }}
                filterOption={(input, option) =>
                  (option.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(value) =>
                  handleFilterChange("selectedBranch", value)
                }
                value={filters.selectedBranch}
                allowClear
              >
                {branches.map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.name}
                  </Select.Option>
                ))}
              </Select>
            )}
            <Select
              bordered
              showSearch
              placeholder={
                serviceListLoading ? "Loading..." : "Select Service Type"
              }
              style={{ width: 200, borderRadius: 6, marginRight: 10 }}
              filterOption={(input, option) =>
                (option.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                // Convert single value to array format for consistency
                handleFilterChange("serviceType", value ? [value] : []);
              }}
              value={
                Array.isArray(filters.serviceType) &&
                filters.serviceType.length === 1
                  ? filters.serviceType[0]
                  : filters.serviceType && !Array.isArray(filters.serviceType)
                  ? filters.serviceType
                  : undefined
              }
              allowClear
              loading={serviceListLoading}
              disabled={serviceListLoading}
              notFoundContent={
                serviceListLoading ? "Loading..." : "No service types found"
              }
            >
              {serviceList.map((item) => (
                <Select.Option
                  key={item?.id}
                  value={item?.id}
                  label={item?.name}
                >
                  {item?.name}
                </Select.Option>
              ))}
            </Select>
            <Select
              bordered
              showSearch
              placeholder="Select Country"
              style={{ width: 200, borderRadius: 6, marginRight: 10 }}
              filterOption={(input, option) =>
                (option.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              onChange={(value) => handleFilterChange("selectedCountry", value)}
              value={filters.selectedCountry}
              allowClear
            >
              {country.map((option) => (
                <Select.Option key={option.code} value={option.code}>
                  {option.name}
                </Select.Option>
              ))}
            </Select>
            <DatePicker.RangePicker
              style={{ width: 200, borderRadius: 6, marginRight: 10 }}
              placeholder={["Start Date", "End Date"]}
              value={filters.dateRange}
              onChange={(value) =>
                handleFilterChange("dateRange", value || [null, null])
              }
            />
          </>
        )}
        <div className="stock-searchinput">
          <Input
            allowClear
            placeholder="Search Box"
            onPressEnter={(e) => handleSearchChange(e.target.value)}
            onChange={(e) => handleSearchChange(e.target.value)}
            prefix={<AiOutlineSearch color="#999" />}
            style={{ borderRadius: 6, width: 200 }}
          />
          <div style={{ margin: 5 }} />
          <div className="stock-filter" onClick={() => setFilter(!filter)}>
            <FaFilter />
          </div>
        </div>
      </div>
      {loading ? (
        <LoadingBox />
      ) : (
        <DataTable
          reload={() => loadData(page, take, filters)}
          meta={meta}
          data={data}
          fullData={fullData}
          PageOnchange={(newPage, newTake) => {
            const validPage = Math.max(1, Number(newPage) || 1);
            const validTake = Number(newTake) || take;
            window.scrollTo(0, 0);
            setPage(validPage);
            setTake(validTake);
            loadData(validPage, validTake, filters);
          }}
          scroll={{ x: "max-content" }}
        />
      )}

      {/* Update Stock Modal */}
      <UpdateStockModal
        visible={updateStockModalVisible}
        onCancel={() => setUpdateStockModalVisible(false)}
        onSuccess={handleUpdateStockSuccess}
      />
    </div>
  );
}

export default Stock;
